import { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import api from '../../services/api';
import {
  Plus, Pencil, Trash2, RefreshCw, Save, X, ToggleLeft, ToggleRight,
  AlertTriangle, Disc3
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

const COLOR_OPTIONS = [
  '#E11D48', '#F59E0B', '#10B981', '#3B82F6', '#8B5CF6',
  '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1', '#FCD34D',
];

const EMPTY = {
  label: '',
  chance: 0,
  color: '#E11D48',
  sort_order: 0,
  is_active: true,
};

export default function RouletteSegmentsPage() {
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/admin/roulette-segments');
      setSegments(res.data?.segments || []);
    } catch {
      toast.error('Failed to load roulette segments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const totalChance = useMemo(
    () => segments.filter(s => s.is_active).reduce((sum, s) => sum + Number(s.chance || 0), 0),
    [segments]
  );
  const isBalanced = Math.abs(totalChance - 100) < 0.01;

  const seedDefaults = async () => {
    try {
      const res = await api.post('/api/admin/roulette-segments/seed-defaults');
      toast.success(`Seeded ${res.data?.created || 0} default segments`);
      load();
    } catch {
      toast.error('Seed failed');
    }
  };

  const startNew = () => {
    setEditing('new');
    setForm({ ...EMPTY, sort_order: segments.length + 1 });
  };

  const startEdit = (s) => {
    setEditing(s);
    setForm({
      label: s.label, chance: s.chance, color: s.color,
      sort_order: s.sort_order, is_active: s.is_active,
    });
  };

  const cancelEdit = () => { setEditing(null); setForm(EMPTY); };

  const save = async () => {
    if (!form.label.trim()) { toast.error('Label is required'); return; }
    if (form.chance < 0 || form.chance > 100) { toast.error('Chance must be 0–100'); return; }
    setSaving(true);
    try {
      if (editing === 'new') {
        await api.post('/api/admin/roulette-segments', form);
        toast.success('Segment created');
      } else {
        await api.put(`/api/admin/roulette-segments/${editing.id}`, form);
        toast.success('Segment updated');
      }
      await load();
      cancelEdit();
    } catch {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (s) => {
    try {
      await api.put(`/api/admin/roulette-segments/${s.id}`, { ...s, is_active: !s.is_active });
      load();
    } catch {
      toast.error('Toggle failed');
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete segment "${s.label}"?`)) return;
    try {
      await api.delete(`/api/admin/roulette-segments/${s.id}`);
      toast.success('Deleted');
      load();
    } catch {
      toast.error('Delete failed');
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-roulette">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Disc3 className="w-6 h-6 text-rose-400" /> Roulette Segments
          </h2>
          <p className="text-sm text-gray-400">Configure prize wedges and winning chances on the player Roulette wheel.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} className="border-white/10" data-testid="rs-reload">
            <RefreshCw className="w-4 h-4 mr-1" /> Reload
          </Button>
          {segments.length === 0 && (
            <Button variant="outline" onClick={seedDefaults} className="border-emerald-500/40 text-emerald-300" data-testid="rs-seed">
              Seed Defaults
            </Button>
          )}
          <Button onClick={startNew} className="bg-rose-600 hover:bg-rose-700" data-testid="rs-new">
            <Plus className="w-4 h-4 mr-1" /> New Segment
          </Button>
        </div>
      </div>

      {/* Total chance banner */}
      <div
        className="rounded-xl px-4 py-3 flex items-center gap-3"
        style={{
          background: isBalanced ? 'rgba(34,197,94,0.10)' : 'rgba(239,68,68,0.10)',
          border: `1px solid ${isBalanced ? 'rgba(34,197,94,0.45)' : 'rgba(239,68,68,0.45)'}`,
        }}
        data-testid="rs-total-chance"
      >
        {!isBalanced && <AlertTriangle className="w-5 h-5 text-rose-400 shrink-0" />}
        <div className="flex-1 text-sm">
          <span className="font-bold" style={{ color: isBalanced ? '#22FF88' : '#FCA5A5' }}>
            Total active chance: {totalChance.toFixed(2)}%
          </span>
          <span className="text-gray-400 ml-2">
            {isBalanced
              ? '— wheel is balanced.'
              : `— should equal 100% for accurate weighted draws (off by ${(totalChance - 100).toFixed(2)}%).`}
          </span>
        </div>
      </div>

      {/* Editor */}
      {editing && (
        <div className="rounded-2xl p-5 bg-[#131822] border border-white/10" data-testid="rs-editor">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold">{editing === 'new' ? 'New Segment' : 'Edit Segment'}</h3>
            <button onClick={cancelEdit} className="p-2 rounded-lg hover:bg-white/5"><X className="w-4 h-4" /></button>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Label" full>
              <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="e.g. $5 Freeplay" className="bg-[#0B0E14] border-white/10" data-testid="rs-field-label" />
            </Field>
            <Field label="Chance (%)">
              <Input type="number" step="0.01" min="0" max="100" value={form.chance}
                onChange={(e) => setForm({ ...form, chance: parseFloat(e.target.value || '0') })}
                className="bg-[#0B0E14] border-white/10" data-testid="rs-field-chance" />
            </Field>
            <Field label="Sort order">
              <Input type="number" value={form.sort_order}
                onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value || '0', 10) })}
                className="bg-[#0B0E14] border-white/10" data-testid="rs-field-order" />
            </Field>
            <Field label="Wedge color" full>
              <div className="flex flex-wrap gap-2">
                {COLOR_OPTIONS.map((c) => {
                  const sel = form.color === c;
                  return (
                    <button key={c} type="button" onClick={() => setForm({ ...form, color: c })}
                      className="w-9 h-9 rounded-lg transition"
                      style={{ background: c, border: sel ? '3px solid #fff' : '1px solid rgba(255,255,255,0.15)' }}
                      data-testid={`rs-color-${c.replace('#','')}`} aria-label={c} />
                  );
                })}
                <Input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })}
                  className="bg-[#0B0E14] border-white/10 w-32" placeholder="#hex" />
              </div>
            </Field>
            <Field label="Status" full>
              <button type="button" onClick={() => setForm({ ...form, is_active: !form.is_active })}
                className="px-4 py-2 rounded-lg flex items-center gap-2 transition w-fit"
                style={{
                  background: form.is_active ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
                  border: `1px solid ${form.is_active ? 'rgba(34,197,94,0.45)' : 'rgba(239,68,68,0.45)'}`,
                  color: form.is_active ? '#22FF88' : '#FCA5A5',
                }}
                data-testid="rs-toggle-active">
                {form.is_active ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                {form.is_active ? 'Active' : 'Inactive'}
              </button>
            </Field>
          </div>
          <div className="flex gap-2 justify-end mt-5">
            <Button variant="outline" onClick={cancelEdit} className="border-white/10">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-rose-600 hover:bg-rose-700" data-testid="rs-save">
              <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving...' : 'Save Segment'}
            </Button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="rounded-2xl border border-white/10 overflow-hidden bg-[#131822]">
        <table className="w-full text-sm">
          <thead className="bg-[#0B0E14] text-gray-400 uppercase tracking-wider text-[11px]">
            <tr>
              <th className="px-3 py-3 text-left">Order</th>
              <th className="px-3 py-3 text-left">Color</th>
              <th className="px-3 py-3 text-left">Label</th>
              <th className="px-3 py-3 text-right">Chance</th>
              <th className="px-3 py-3 text-center">Active</th>
              <th className="px-3 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-gray-400">Loading...</td></tr>
            )}
            {!loading && segments.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-gray-400">
                No segments yet. Click <span className="text-rose-400">Seed Defaults</span> for the standard 11-prize wheel, or <span className="text-rose-400">New Segment</span>.
              </td></tr>
            )}
            {segments.map((s) => (
              <tr key={s.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`rs-row-${s.id}`}>
                <td className="px-3 py-3">{s.sort_order}</td>
                <td className="px-3 py-3">
                  <span className="inline-block w-6 h-6 rounded" style={{ background: s.color, border: '1px solid rgba(255,255,255,0.15)' }} />
                </td>
                <td className="px-3 py-3 font-bold">{s.label}</td>
                <td className="px-3 py-3 text-right font-mono">{Number(s.chance).toFixed(2)}%</td>
                <td className="px-3 py-3 text-center">
                  <button onClick={() => toggleActive(s)} className="inline-flex items-center" data-testid={`rs-toggle-${s.id}`}>
                    {s.is_active
                      ? <ToggleRight className="w-6 h-6 text-emerald-400" />
                      : <ToggleLeft className="w-6 h-6 text-gray-500" />}
                  </button>
                </td>
                <td className="px-3 py-3 text-right">
                  <button onClick={() => startEdit(s)} className="p-2 rounded-lg hover:bg-white/5" data-testid={`rs-edit-${s.id}`}>
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => remove(s)} className="p-2 rounded-lg hover:bg-red-500/10 text-red-400" data-testid={`rs-delete-${s.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? 'sm:col-span-2' : ''}>
      <label className="text-xs uppercase tracking-wider text-gray-400 mb-1 block">{label}</label>
      {children}
    </div>
  );
}
